<template>
  <v-col cols="12">
    <div>
      <naj-box
        :no-header="hideNajBox"
        :no-gutters="hideNajBox"
        :no-margin-bottom="hideNajBox"
        :title="locales.sectionTitle"
      >
        <v-row>
          <v-col cols="4">
            <naj-input
              :type-id="formFieldType.text"
              :label="locales.socialId"
              :value.sync="socialIdValue"
              :disabled="isChecking || viewMode"
              validates="required|numeric|max:10"
              @input="socialIdChanged"
            ></naj-input>
          </v-col>
          <v-col
            cols="4"
            align-self="center"
          >
            <naj-button
              v-if="isCheckButtonVisible"
              square
              :loading="isChecking"
              :disabled="isCheckButtonDisabled || viewMode"
              @click="check"
            >
              {{ local.personInformation.check }}
            </naj-button>
          </v-col>
        </v-row>
      </naj-box>
    </div>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { casePartyRoleEnum } from '@/helpers/enum';
import { RELATIONSHIP_DATA_SOURCE } from '../check-heirs-data/heirs-grid';
const { mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');

// Mapping from heirs service relationship names to dropdown data source entries
const RELATIONSHIP_MAP = {
  // Service names (with/without ال)
  'أب': { id: 1, name: 'أبيه' },
  'الأب': { id: 1, name: 'أبيه' },
  'أم': { id: 7, name: 'أمه' },
  'الأم': { id: 7, name: 'أمه' },
  'ابن': { id: 2, name: 'ابنه' },
  'الابن': { id: 2, name: 'ابنه' },
  'ابنة': { id: 3, name: 'ابنته' },
  'الابنة': { id: 3, name: 'ابنته' },
  'أخ': { id: 5, name: 'أخوه' },
  'الأخ': { id: 5, name: 'أخوه' },
  'الأخ الشقيق': { id: 18, name: 'أخيه الشقيق' },
  'الأخ لأم': { id: 30, name: 'أخيه لأم' },
  'الأخ لأب': { id: 29, name: 'أخيه لأبيه' },
  'أخت': { id: 8, name: 'أخته' },
  'الأخت': { id: 8, name: 'أخته' },
  'الأخت الشقيقة': { id: 19, name: 'أخته الشقيقة' },
  'الأخت لأم': { id: 59, name: 'أخته لأمه' },
  'الأخت لأب': { id: 50, name: 'أخته لأبيه' },
  'زوج': { id: 74, name: 'زوج أمه' },
  'الزوج': { id: 74, name: 'زوج أمه' },
  'زوجة': { id: 9, name: 'زوجته' },
  'الزوجة': { id: 9, name: 'زوجته' },
  'العم': { id: 11, name: 'عمه' },
  'العمة': { id: 38, name: 'عمته' },
  'الخال': { id: 4, name: 'خاله' },
  'الخالة': { id: 22, name: 'خالته' },
  'الجد': { id: 58, name: 'جده' },
  'ابن الابن': { id: 25, name: 'ابن ابنه' },
  'بنت الابن': { id: 24, name: 'بنت ابنه' },
};

// Status name to key mapping (service always sends male names)
const STATUS_NAME_TO_KEY = {
  'راشد': 1,
  'قاصر سناً': 2,
  ' قاصر سناً': 2,
  'قاصر سنا': 2,
  'قاصر عقلاً': 3,
  'قاصر عقلا': 3,
  'متوفى': 4,
  'متوفي': 4,
};

const MALE_STATUS_BY_KEY = {
  1: 'راشد',
  2: ' قاصر سناً',
  3: 'قاصر عقلاً',
  4: 'متوفى',
};

const FEMALE_STATUS_BY_KEY = {
  1: 'راشدة',
  2: 'قاصرة سنًا',
  3: 'قاصرة عقلًا',
  4: 'متوفية',
};

export default {
  name: 'InheritorInquiry',
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    rootFormModel: {
      type: Object,
      required: true,
    },
    hideNajBox: {
      type: Boolean,
      required: false,
    },
    viewMode: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      isChecking: false,
      isManual: false,
      socialIdValue: this.formModel.inheritorSocialId || '',
    };
  },
  computed: {
    locales() {
      return this.local.dynamicFormAdmin.customComponents.inheritorInquiry;
    },
    isCheckButtonDisabled() {
      return (
        !this.socialIdValue ||
        this.socialIdValue.length !== 10 ||
        this.isChecking
      );
    },
    isCheckButtonVisible() {
      return !this.formModel.isInheritorVerified || this.isChecking;
    },
  },
  watch: {
    socialIdValue(val) {
      this.$set(this.formModel, 'inheritorSocialId', val);
    },
  },
  methods: {
    ...mapGlobalActions(['getDeceasedAndHeirsDataForDefendant']),
    check() {
      this.isChecking = true;
      this.resetForm();
      this.getDeceasedAndHeirsDataForDefendant({
        socialId: this.socialIdValue,
      })
        .then((res) => {
          if (res.data && res.data.deceased) {
            const deceased = res.data.deceased;
            const heirs = res.data.heirs || [];
            // Check if prosecutor is among the heirs or represents one of them
            // If not, leave form empty for manual entry
            if (!this.isProsecutorAmongHeirs(heirs)) {
              this.toaster.error('يرجى إدخال بيانات الورثة يدوياً, لعدم عرض بيانات الورثة إلا للمعنيين من الورثة');
              return;
            }
            this.$set(this.formModel, 'isInheritorVerified', true);
            this.toaster.success(this.local.common.verificationSucceeded);
            const mappedData = {
              inheritorName: deceased.fullName,
              deathDate: deceased.deathDate,
              inheritorGender: deceased.genderId,
              inheritorGenderName: deceased.gender,
              inheritorSocialId: deceased.socialId,
              issuer: res.data.courtName || null,
              instrumentNumber: res.data.requestCode || null,
            };
            Object.keys(mappedData).forEach((key) => {
              this.$set(this.formModel, key, mappedData[key]);
            });
            this.$set(this.formModel, 'heirsData', this.mapHeirsData(heirs));
          } else {
            this.isManual = true;
            this.toaster.error(this.local.common.noData);
          }
        })
        .catch(() => {
          this.isManual = true;
          this.toaster.error(this.local.common.noData);
        })
        .finally(() => {
          this.isChecking = false;
        });
    },
    resetForm() {
      if (this.formModel.isInheritorVerified) {
        this.$set(this.formModel, 'inheritorName', null);
        this.$set(this.formModel, 'deathDate', null);
        this.$set(this.formModel, 'inheritorGender', null);
        this.$set(this.formModel, 'inheritorGenderName', null);
        this.$set(this.formModel, 'issuer', null);
        this.$set(this.formModel, 'instrumentNumber', null);
        this.$set(this.formModel, 'instrumentDate', null);
        this.$set(this.formModel, 'heirsData', null);
      }
      this.$set(this.formModel, 'isInheritorVerified', false);
      this.isManual = false;
    },
    socialIdChanged() {
      this.resetForm();
    },
    resolveHeirStatus(statusId, statusName, genderId) {
      let key = statusId;
      // If no statusId, resolve from male status name (service always sends male names)
      if (!key && statusName) {
        key = STATUS_NAME_TO_KEY[statusName.trim()] || null;
      }
      if (!key) return { key: null, name: statusName || null };
      const isFemale = Number(genderId) === 2;
      const name = isFemale
        ? (FEMALE_STATUS_BY_KEY[key] || statusName)
        : (MALE_STATUS_BY_KEY[key] || statusName);
      return { key, name };
    },
    resolveRelationship(serviceName) {
      if (serviceName == null || serviceName === '') return { id: null, name: null };
      const trimmed = String(serviceName).trim();
      // If the service returns a numeric id (e.g. 7 or "7"), look up by id
      const numVal = Number(trimmed);
      if (!isNaN(numVal) && String(numVal) === trimmed) {
        const match = RELATIONSHIP_DATA_SOURCE.find(item => item.id === numVal);
        if (match) return { id: match.id, name: match.name };
      }
      // Direct map lookup
      if (RELATIONSHIP_MAP[trimmed]) return RELATIONSHIP_MAP[trimmed];
      // Fallback: try exact match against the data source
      let match = RELATIONSHIP_DATA_SOURCE.find(
        item => item.name === trimmed || item.value === trimmed
      );
      if (match) return { id: match.id, name: match.name };
      // Try stripping ال prefix and match
      const withoutAl = trimmed.replace(/^ال/, '');
      match = RELATIONSHIP_DATA_SOURCE.find(
        item => item.name === withoutAl || item.value === withoutAl
      );
      if (match) return { id: match.id, name: match.name };
      return { id: null, name: trimmed };
    },
    isProsecutorAmongHeirs(heirs) {
      try {
        const lawsuitState = this.$store.state.LawsuitModule;
        if (!lawsuitState || !lawsuitState.lawsuitRequest) return true;
        const caseParties = lawsuitState.lawsuitRequest.caseParties || [];
        const prosecutors = caseParties.filter(
          cp => Number(cp.casePartyRole) === casePartyRoleEnum.prosecutor
        );
        if (!prosecutors.length) return true;
        const heirSocialIds = heirs.map(h => String(h.socialId));
        // Check if any prosecutor is one of the heirs
        const prosecutorIsHeir = prosecutors.some(
          p => heirSocialIds.includes(String(p.socialId))
        );
        if (prosecutorIsHeir) return true;
        // Check if any prosecutor represents one of the heirs (electronic wakala)
        const representatives = caseParties.filter(
          cp => Number(cp.casePartyRole) === casePartyRoleEnum.agent ||
                Number(cp.casePartyRole) === casePartyRoleEnum.representative
        );
        const representedBySomeone = representatives.some(rep => {
          const relatedParties = rep.relatedPartiesInfo || [];
          return relatedParties.some(rp => heirSocialIds.includes(String(rp.socialId)));
        });
        return representedBySomeone;
      } catch (e) {
        // If store is not available or an error occurs, allow auto-fill
        return true;
      }
    },
    mapHeirsData(apiData) {
      return apiData.map((item, index) => {
        const relationship = this.resolveRelationship(item.relationToDeceasedName);
        const status = this.resolveHeirStatus(item.statusId, item.statusName, item.genderId);
        return {
          heirGender: item.genderId,
          heirGenderName: item.gender,
          deceasedRelationship: relationship.name,
          deceasedrelationship: relationship.name,
          deceasedRelationshipName: relationship.name,
          heirName: item.fullName,
          idNumber: item.socialId,
          birthDate: item.dateOfBirth,
          heirCondition: status.key,
          heirConditionName: status.name,
          heirdeathDate: item.deathDate || null,
          issuerSubGrid: null,
          instrumentNumberSubGrid: null,
          dateSubGrid: null,
          heirsSubGrid: null,
          objectKey: item.personId,
          number: index + 1,
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
